import React, { useEffect, useMemo, useState } from "react";
import {
    RefreshCw,
    Search,
    Eye,
    Loader2,
    ShieldCheck,
    ClipboardList,
    CheckCircle2,
    Clock3,
    AlertTriangle,
    X,
    MapPin,
    CalendarDays,
    FileText,
    Image as ImageIcon,
    Activity,
    User,
    Filter,
    Lock,
} from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";


const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    "https://api.schoolpay.tech";

const PBT_API = {
    cases: "/pbt/case",
    caseDetail: (caseUuid) => `/pbt/case/${caseUuid}`,

    // Change this path to your actual unlock/view endpoint if different.
    attachmentView: (attachmentUuid) => `/pbt/attachment/${attachmentUuid}/view`,
};
const COLORS = {
    navy: "#062E63",
    blue: "#0B4DA2",
    bg: "#F3F6FA",
    surface: "#FFFFFF",
    border: "#E2E8F0",
    text: "#0F172A",
    muted: "#64748B",
    soft: "#94A3B8",
    red: "#DC2626",
    redBg: "#FEF2F2",
    orange: "#F97316",
    amber: "#B45309",
    amberBg: "#FFFBEB",
    green: "#059669",
    greenBg: "#ECFDF5",
    purple: "#7C3AED",
    purpleBg: "#F5F3FF",
};
const glowingIcon = L.divIcon({
    className: "glowing-map-marker",
    html: `
        <div class="glow-marker-wrap">
            <div class="glow-marker-pulse"></div>
            <div class="glow-marker-dot"></div>
        </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

function getToken() {
    return localStorage.getItem("token") || "";
}

function getSchoolId() {
    return (
        localStorage.getItem("id_code") ||
        localStorage.getItem("school_id") ||
        localStorage.getItem("schoolId") ||
        ""
    );
}

function buildQuery(params = {}) {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        searchParams.append(key, String(value));
    });

    const qs = searchParams.toString();
    return qs ? `?${qs}` : "";
}

async function apiRequest(path, options = {}) {
    const token = getToken();

    const res = await fetch(`${API_BASE_URL}${path}`, {
        method: options.method || "GET",
        headers: {
            "Content-Type": "application/json",
            token,
            ...(options.headers || {}),
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const raw = await res.text();

    let json = {};
    try {
        json = raw ? JSON.parse(raw) : {};
    } catch (err) {
        throw new Error("Invalid API response.");
    }

    if (!res.ok) {
        throw new Error(json?.message || `Request failed: ${res.status}`);
    }

    if (json?.response_code && json.response_code !== 1000) {
        throw new Error(json?.message || "Request failed.");
    }

    return json;
}
async function unlockAttachmentUrl(attachment, schoolId) {
    const token = getToken();

    if (!attachment?.uuid) {
        return "";
    }

    const params = buildQuery({
        school_id: schoolId,
    });

    const requestUrl = `${API_BASE_URL}${PBT_API.attachmentView(attachment.uuid)}${params}`;

    let res;

    try {
        res = await fetch(requestUrl, {
            method: "GET",
            headers: {
                token,
                "Content-Type": "application/json",
            },
        });
    } catch (err) {
        console.error("Attachment fetch failed:", {
            url: requestUrl,
            error: err,
        });

        throw new Error(
            "Gagal fetch attachment API. Semak URL API, CORS, atau backend route."
        );
    }

    const raw = await res.text();

    let json = {};
    try {
        json = raw ? JSON.parse(raw) : {};
    } catch (err) {
        console.error("Attachment API returned non-JSON:", {
            url: requestUrl,
            status: res.status,
            raw,
        });

        throw new Error("Attachment API returned invalid JSON.");
    }

    if (!res.ok) {
        console.error("Attachment API HTTP error:", {
            url: requestUrl,
            status: res.status,
            response: json,
        });

        throw new Error(json?.message || `Gagal membuka lampiran: ${res.status}`);
    }

    if (json?.response_code && json.response_code !== 1000) {
        console.error("Attachment API response_code error:", {
            url: requestUrl,
            response: json,
        });

        throw new Error(json?.message || "Gagal membuka lampiran.");
    }

    const mimeType = json?.data?.mime_type || "image/jpeg";
    const imageData = json?.data?.image_data;

    // Your backend returns this.
    if (imageData) {
        if (String(imageData).startsWith("data:image")) {
            return imageData;
        }

        return `data:${mimeType};base64,${imageData}`;
    }

    // Fallback for signed URL style response.
    return (
        json?.data?.url ||
        json?.data?.file_url ||
        json?.data?.image_url ||
        json?.data?.attachment_url ||
        json?.data?.signed_url ||
        json?.data?.presigned_url ||
        json?.data?.image ||
        ""
    );
}

function formatDate(value) {
    if (!value) return "-";

    try {
        let raw = String(value).trim();

        // Convert MySQL datetime format to ISO-like format
        raw = raw.replace(" ", "T");

        // If backend returns UTC without timezone, force it as UTC
        // Example: 2026-06-05T06:30:00 -> 2026-06-05T06:30:00Z
        const hasTimezone =
            raw.endsWith("Z") ||
            /[+-]\d{2}:\d{2}$/.test(raw);

        const date = new Date(hasTimezone ? raw : `${raw}Z`);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString("ms-MY", {
            timeZone: "Asia/Kuala_Lumpur",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    } catch (err) {
        return value;
    }
}

function formatStatus(status) {
    const value = String(status || "OPEN").toUpperCase();

    if (value === "COMPLETED") return "Selesai";
    if (value === "IN_PROGRESS") return "Sedang Diproses";
    if (value === "OPEN") return "Dibuka";
    if (value === "REJECTED") return "Ditolak";
    if (value === "CANCELLED") return "Dibatalkan";
    if (value === "PENDING") return "Menunggu";
    if (value === "SKIPPED") return "Dilangkau";

    return value;
}

function formatPriority(priority) {
    const value = String(priority || "MEDIUM").toUpperCase();

    if (value === "URGENT") return "Segera";
    if (value === "HIGH") return "Tinggi";
    if (value === "MEDIUM") return "Sederhana";
    if (value === "LOW") return "Rendah";

    return value;
}

function statusStyle(status) {
    const value = String(status || "").toUpperCase();

    if (value === "COMPLETED") {
        return {
            background: COLORS.greenBg,
            color: "#065F46",
            borderColor: "#A7F3D0",
        };
    }

    if (value === "OPEN" || value === "IN_PROGRESS" || value === "PENDING") {
        return {
            background: COLORS.amberBg,
            color: COLORS.amber,
            borderColor: "#FDE68A",
        };
    }

    if (value === "REJECTED" || value === "CANCELLED") {
        return {
            background: COLORS.redBg,
            color: COLORS.red,
            borderColor: "#FECACA",
        };
    }

    return {
        background: "#EFF6FF",
        color: "#1E3A8A",
        borderColor: "#BFDBFE",
    };
}

function priorityStyle(priority) {
    const value = String(priority || "").toUpperCase();

    if (value === "URGENT" || value === "HIGH") {
        return {
            background: COLORS.redBg,
            color: COLORS.red,
            borderColor: "#FECACA",
        };
    }

    if (value === "MEDIUM") {
        return {
            background: COLORS.amberBg,
            color: COLORS.amber,
            borderColor: "#FDE68A",
        };
    }

    return {
        background: "#F8FAFC",
        color: "#475569",
        borderColor: COLORS.border,
    };
}

function Badge({ children, style }) {
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "5px 9px",
                fontSize: 11,
                fontWeight: 900,
                border: "1px solid",
                whiteSpace: "nowrap",
                ...style,
            }}
        >
            {children}
        </span>
    );
}

function StatCard({ title, value, subtitle, icon, color }) {
    return (
        <div
            style={{
                background: COLORS.surface,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 22,
                padding: 18,
                boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                    <div
                        style={{
                            fontSize: 13,
                            color: COLORS.muted,
                            fontWeight: 800,
                            marginBottom: 8,
                        }}
                    >
                        {title}
                    </div>

                    <div
                        style={{
                            fontSize: 30,
                            lineHeight: "34px",
                            color: COLORS.text,
                            fontWeight: 950,
                        }}
                    >
                        {value || 0}
                    </div>

                    <div
                        style={{
                            marginTop: 7,
                            fontSize: 12,
                            color: COLORS.soft,
                            fontWeight: 700,
                        }}
                    >
                        {subtitle}
                    </div>
                </div>

                <div
                    style={{
                        width: 46,
                        height: 46,
                        borderRadius: 16,
                        background: `${color}12`,
                        color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </div>
            </div>
        </div>
    );
}

function PrimaryButton({ children, onClick, disabled, icon }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            style={{
                border: 0,
                borderRadius: 14,
                padding: "11px 15px",
                background: disabled
                    ? "#CBD5E1"
                    : `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
                color: "#FFFFFF",
                fontWeight: 900,
                fontSize: 13,
                cursor: disabled ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            }}
        >
            {icon}
            {children}
        </button>
    );
}

function SecondaryButton({ children, onClick, icon, disabled }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 14,
                padding: "10px 14px",
                background: disabled ? "#F8FAFC" : "#FFFFFF",
                color: disabled ? COLORS.soft : COLORS.text,
                fontWeight: 900,
                fontSize: 13,
                cursor: disabled ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            }}
        >
            {icon}
            {children}
        </button>
    );
}

function ModalShell({ title, subtitle, open, onClose, children }) {
    if (!open) return null;

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                background: "rgba(15,23,42,0.58)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 20,
            }}
            onMouseDown={onClose}
        >
            <div
                style={{
                    width: "min(1180px, 96vw)",
                    maxHeight: "92vh",
                    overflow: "auto",
                    background: "#FFFFFF",
                    borderRadius: 24,
                    boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        padding: 20,
                        background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
                        color: "#FFFFFF",
                        borderTopLeftRadius: 24,
                        borderTopRightRadius: 24,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 16,
                    }}
                >
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 950 }}>{title}</div>
                        <div
                            style={{
                                marginTop: 5,
                                color: "rgba(255,255,255,0.75)",
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            {subtitle}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 12,
                            border: "1px solid rgba(255,255,255,0.2)",
                            background: "rgba(255,255,255,0.12)",
                            color: "#FFFFFF",
                            cursor: "pointer",
                        }}
                    >
                        <X size={19} />
                    </button>
                </div>

                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

function DetailItem({ label, value, icon }) {
    return (
        <div
            style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 16,
                padding: 13,
                background: "#FFFFFF",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 12,
                    color: COLORS.muted,
                    fontWeight: 850,
                    marginBottom: 6,
                }}
            >
                {icon}
                {label}
            </div>

            <div
                style={{
                    fontSize: 14,
                    color: COLORS.text,
                    fontWeight: 900,
                    wordBreak: "break-word",
                }}
            >
                {value || "-"}
            </div>
        </div>
    );
}

function renderPayload(payload) {
    if (!payload || typeof payload !== "object") {
        return (
            <div style={{ color: COLORS.soft, fontSize: 13, fontWeight: 750 }}>
                Tiada data dihantar.
            </div>
        );
    }

    const HIDDEN_KEYS = new Set([
        "url",
        "file_url",
        "image_url",
        "attachment_url",
        "s3_url",
        "photo",
        "image",
        "image_data",
        "file_path",
        "path",
        "fields",
        "verification",
        "face_image",
        "face_verification",
        "captured_image",
        "base64",
    ]);

    const LABEL_MAP = {
        type: "Jenis Lampiran",
        remarks: "Catatan",
        latitude: "Latitude",
        longitude: "Longitude",
        gps_accuracy: "Ketepatan GPS",
        accuracy: "Ketepatan GPS",
        profile_uuid: "Profile UUID",
        submitted_at: "Tarikh Dihantar",
        submitted_by: "Dihantar Oleh",
        file_name: "Nama Fail",
        mime_type: "Jenis Fail",
        captured_at: "Masa Diambil",
        name: "Nama",
    };

    const cleanValue = (value) => {
        if (value === undefined || value === null || value === "") return "-";

        if (typeof value === "object") {
            const cleaned = Object.entries(value).filter(
                ([key]) => !HIDDEN_KEYS.has(String(key).toLowerCase())
            );

            if (!cleaned.length) return "-";

            return cleaned
                .map(([key, val]) => {
                    const label = LABEL_MAP[key] || key.replace(/_/g, " ");
                    return `${label}: ${typeof val === "object" ? JSON.stringify(val) : val}`;
                })
                .join("\n");
        }

        return String(value);
    };

    const entries = Object.entries(payload).filter(([key, value]) => {
        const lowerKey = String(key).toLowerCase();

        if (HIDDEN_KEYS.has(lowerKey)) return false;
        if (value === undefined || value === null || value === "") return false;

        return true;
    });

    if (!entries.length) {
        return (
            <div style={{ color: COLORS.soft, fontSize: 13, fontWeight: 750 }}>
                Tiada maklumat tambahan.
            </div>
        );
    }

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
            }}
        >
            {entries.map(([key, value]) => (
                <div
                    key={key}
                    style={{
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 13,
                        padding: 10,
                        background: "#F8FAFC",
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            color: COLORS.muted,
                            fontWeight: 900,
                            marginBottom: 5,
                            textTransform: "uppercase",
                        }}
                    >
                        {LABEL_MAP[key] || key.replace(/_/g, " ")}
                    </div>

                    <div
                        style={{
                            fontSize: 13,
                            color: COLORS.text,
                            fontWeight: 800,
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            lineHeight: "19px",
                        }}
                    >
                        {cleanValue(value)}
                    </div>
                </div>
            ))}
        </div>
    );
}
function getAttachmentUrl(attachment) {
    return (
        attachment?.file_url ||
        attachment?.attachment_url ||
        attachment?.url ||
        attachment?.s3_url ||
        attachment?.image_url ||
        attachment?.path ||
        ""
    );
}
function StepLocationMap({ latitude, longitude, accuracy }) {
    const lat = Number(latitude);
    const lng = Number(longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    return (
        <div
            style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 16,
                overflow: "hidden",
                background: "#FFFFFF",
                marginBottom: 12,
            }}
        >
            <div
                style={{
                    padding: "11px 13px",
                    borderBottom: `1px solid ${COLORS.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 950,
                        color: COLORS.text,
                    }}
                >
                    <MapPin size={15} color={COLORS.blue} />
                    Lokasi GPS
                </div>

                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 850,
                        color: COLORS.muted,
                    }}
                >
                     {accuracy ? `Anggaran lokasi ±${Number(accuracy).toFixed(0)}m` : "Lokasi GPS"}
                </div>
            </div>

            <div
                style={{
                    height: 260,
                    width: "100%",
                    position: "relative",
                }}
            >
                <MapContainer
                    center={[lat, lng]}
                    zoom={17}
                    scrollWheelZoom={true}
                    style={{ height: "100%", width: "100%" }}
                >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker position={[lat, lng]} icon={glowingIcon} />
                </MapContainer>
            </div>

            <div
                style={{
                    padding: "10px 13px",
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                    borderTop: `1px solid ${COLORS.border}`,
                    background: "#F8FAFC",
                }}
            >
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 850,
                        color: COLORS.muted,
                    }}
                >
                    Latitude: <span style={{ color: COLORS.text }}>{lat}</span>
                </div>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 850,
                        color: COLORS.muted,
                    }}
                >
                    Longitude: <span style={{ color: COLORS.text }}>{lng}</span>
                </div>
            </div>
        </div>
    );
}
export default function PbtCaseDashboard() {
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [cases, setCases] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        has_more: false,
    });

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [onlyMine, setOnlyMine] = useState(false);

    const [selectedCase, setSelectedCase] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    const [attachmentPreviewMap, setAttachmentPreviewMap] = useState({});
    const [attachmentLoadingMap, setAttachmentLoadingMap] = useState({});
    const schoolId = getSchoolId();

    const metrics = useMemo(() => {
        const open = cases.filter((item) =>
            ["OPEN", "IN_PROGRESS", "PENDING"].includes(String(item.status || "").toUpperCase())
        ).length;

        const completed = cases.filter(
            (item) => String(item.status || "").toUpperCase() === "COMPLETED"
        ).length;

        const urgent = cases.filter((item) =>
            ["URGENT", "HIGH"].includes(String(item.priority || "").toUpperCase())
        ).length;

        return {
            total: pagination.total || cases.length || 0,
            open,
            completed,
            urgent,
        };
    }, [cases, pagination.total]);

    const filteredCases = useMemo(() => {
        const q = search.trim().toLowerCase();

        return cases.filter((item) => {
            const matchesSearch =
                !q ||
                [
                    item.reference_no,
                    item.title,
                    item.description,
                    item.workflow_name,
                    item.workflow_code,
                    item.location_name,
                    item.status,
                    item.priority,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(q);

            const matchesPriority =
                !priorityFilter ||
                String(item.priority || "").toUpperCase() === priorityFilter.toUpperCase();

            return matchesSearch && matchesPriority;
        });
    }, [cases, search, priorityFilter]);

    const loadCases = async (page = 1) => {
        setLoading(true);

        try {
            const params = {
                school_id: schoolId,
                page,
                limit: pagination.limit || 20,
            };

            if (statusFilter) params.status = statusFilter;
            if (onlyMine) params.created_by_me = 1;

            const json = await apiRequest(`${PBT_API.cases}${buildQuery(params)}`);

            const items = json?.data?.items || [];
            const paging = json?.data?.pagination || {};

            setCases(Array.isArray(items) ? items : []);
            setPagination({
                page: paging.page || page,
                limit: paging.limit || 20,
                total: paging.total || 0,
                has_more: Boolean(paging.has_more),
            });
        } catch (err) {
            alert(err.message || "Gagal memuatkan kes.");
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    const openCaseDetail = async (caseUuid) => {
        if (!caseUuid) return;

        setShowDetailModal(true);
        setDetailLoading(true);
        setSelectedCase(null);

        try {
            const json = await apiRequest(
                `${PBT_API.caseDetail(caseUuid)}${buildQuery({ school_id: schoolId })}`
            );

            setSelectedCase(json?.data || null);
        } catch (err) {
            alert(err.message || "Gagal memuatkan butiran kes.");
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeCaseDetail = () => {
        Object.values(attachmentPreviewMap).forEach((url) => {
            if (String(url || "").startsWith("blob:")) {
                URL.revokeObjectURL(url);
            }
        });

        setShowDetailModal(false);
        setSelectedCase(null);
        setDetailLoading(false);
        setAttachmentPreviewMap({});
        setAttachmentLoadingMap({});
    };

    const unlockAttachment = async (attachment) => {
        if (!attachment?.uuid) {
            alert("Attachment UUID tidak dijumpai.");
            return "";
        }

        if (attachmentPreviewMap[attachment.uuid]) {
            return attachmentPreviewMap[attachment.uuid];
        }

        setAttachmentLoadingMap((prev) => ({
            ...prev,
            [attachment.uuid]: true,
        }));

        try {
            const unlockedUrl = await unlockAttachmentUrl(attachment, schoolId);

            if (!unlockedUrl) {
                throw new Error("API berjaya dipanggil tetapi URL/gambar kosong.");
            }

            setAttachmentPreviewMap((prev) => ({
                ...prev,
                [attachment.uuid]: unlockedUrl,
            }));

            return unlockedUrl;
        } catch (err) {
            console.error("unlockAttachment error:", err);
            alert(err?.message || "Gagal membuka gambar.");
            return "";
        } finally {
            setAttachmentLoadingMap((prev) => ({
                ...prev,
                [attachment.uuid]: false,
            }));
        }
    };
    useEffect(() => {
        loadCases(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, onlyMine]);

    const renderCases = () => {
        if (loading) {
            return (
                <div
                    style={{
                        padding: 48,
                        textAlign: "center",
                        color: COLORS.muted,
                        fontWeight: 850,
                    }}
                >
                    <Loader2
                        size={25}
                        style={{
                            animation: "spin 1s linear infinite",
                            marginBottom: 8,
                        }}
                    />
                    <div>Memuatkan senarai kes...</div>
                </div>
            );
        }

        if (!filteredCases.length) {
            return (
                <div
                    style={{
                        padding: 42,
                        textAlign: "center",
                        background: "#F8FAFC",
                        border: `1px dashed ${COLORS.border}`,
                        borderRadius: 18,
                    }}
                >
                    <ClipboardList size={35} color={COLORS.soft} />
                    <div
                        style={{
                            marginTop: 10,
                            fontSize: 16,
                            fontWeight: 950,
                            color: COLORS.text,
                        }}
                    >
                        Tiada kes dijumpai
                    </div>
                    <div
                        style={{
                            marginTop: 5,
                            fontSize: 13,
                            fontWeight: 700,
                            color: COLORS.muted,
                        }}
                    >
                        Cuba ubah carian, status atau filter.
                    </div>
                </div>
            );
        }

        return (
            <div style={{ overflowX: "auto" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: "0 10px",
                    }}
                >
                    <thead>
                        <tr>
                            {[
                                "Kes / Rekod",
                                "Workflow",
                                "Status",
                                "Prioriti",
                                "Dicipta",
                                "Tindakan",
                            ].map((head) => (
                                <th
                                    key={head}
                                    style={{
                                        textAlign: "left",
                                        fontSize: 11,
                                        color: COLORS.muted,
                                        fontWeight: 950,
                                        padding: "0 12px 4px",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {head}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {filteredCases.map((item) => (
                            <tr
                                key={item.uuid}
                                style={{
                                    background: "#FFFFFF",
                                    boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
                                }}
                            >


                                <td
                                    style={{
                                        padding: 14,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        minWidth: 260,
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 950,
                                            color: COLORS.text,
                                        }}
                                    >
                                        {item.title || "Rekod Operasi"}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: 12,
                                            fontWeight: 900,
                                            color: COLORS.blue,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {item.reference_no || "-"}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 4,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: COLORS.muted,
                                            maxWidth: 360,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {item.description || "Tiada penerangan."}
                                    </div>
                                </td>

                                <td
                                    style={{
                                        padding: 14,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        fontSize: 13,
                                        fontWeight: 850,
                                        color: COLORS.text,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {item.workflow_name || "-"}
                                </td>

                                <td
                                    style={{
                                        padding: 14,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        borderBottom: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <Badge style={statusStyle(item.status)}>{formatStatus(item.status)}</Badge>
                                </td>
                                <td
                                    style={{
                                        padding: 14,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        borderBottom: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <Badge style={priorityStyle(item.priority)}>
                                        {formatPriority(item.priority)}
                                    </Badge>
                                </td>
                                <td
                                    style={{
                                        padding: 14,
                                        borderTop: `1px solid ${COLORS.border}`,
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        fontSize: 13,
                                        color: COLORS.muted,
                                        fontWeight: 800,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {formatDate(item.created_at)}
                                </td>

                                <td
                                    style={{
                                        padding: 14,
                                        borderTopRightRadius: 16,
                                        borderBottomRightRadius: 16,
                                        border: `1px solid ${COLORS.border}`,
                                        borderLeft: 0,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <PrimaryButton
                                        onClick={() => openCaseDetail(item.uuid)}
                                        icon={<Eye size={15} />}
                                    >
                                        Lihat Detail
                                    </PrimaryButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderCaseDetail = () => {
        if (detailLoading) {
            return (
                <div
                    style={{
                        padding: 50,
                        textAlign: "center",
                        color: COLORS.muted,
                        fontWeight: 850,
                    }}
                >
                    <Loader2
                        size={26}
                        style={{
                            animation: "spin 1s linear infinite",
                            marginBottom: 8,
                        }}
                    />
                    <div>Memuatkan butiran kes...</div>
                </div>
            );
        }

        if (!selectedCase) {
            return (
                <div
                    style={{
                        padding: 40,
                        textAlign: "center",
                        color: COLORS.muted,
                        fontWeight: 850,
                    }}
                >
                    Tiada butiran kes.
                </div>
            );
        }

        const steps = Array.isArray(selectedCase.steps) ? selectedCase.steps : [];
        const logs = Array.isArray(selectedCase.logs) ? selectedCase.logs : [];

        return (
            <div>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1.3fr 0.7fr",
                        gap: 16,
                    }}
                >
                    <div
                        style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 20,
                            padding: 18,
                            background: "#F8FAFC",
                        }}
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                            <Badge style={statusStyle(selectedCase.status)}>
                                {formatStatus(selectedCase.status)}
                            </Badge>

                            <Badge style={priorityStyle(selectedCase.priority)}>
                                {formatPriority(selectedCase.priority)}
                            </Badge>

                            <Badge
                                style={{
                                    background: "#EFF6FF",
                                    color: "#1E3A8A",
                                    borderColor: "#BFDBFE",
                                }}
                            >
                                {selectedCase.reference_no || "-"}
                            </Badge>
                        </div>

                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 950,
                                color: COLORS.text,
                                letterSpacing: -0.5,
                            }}
                        >
                            {selectedCase.title || "Rekod Operasi"}
                        </div>

                        <div
                            style={{
                                marginTop: 8,
                                fontSize: 14,
                                color: COLORS.muted,
                                fontWeight: 700,
                                lineHeight: "22px",
                            }}
                        >
                            {selectedCase.description || "Tiada penerangan."}
                        </div>
                    </div>

                    <div
                        style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 20,
                            padding: 18,
                            background: "#FFFFFF",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 950,
                                color: COLORS.text,
                                marginBottom: 12,
                            }}
                        >
                            Ringkasan Workflow
                        </div>

                        <div style={{ display: "grid", gap: 8 }}>
                            <DetailItem
                                label="Workflow"
                                value={selectedCase.workflow_name}
                                icon={<Activity size={14} />}
                            />
                            <DetailItem
                                label="Langkah Semasa"
                                value={selectedCase.current_step_key}
                                icon={<Clock3 size={14} />}
                            />
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 12,
                        marginTop: 16,
                    }}
                >
                    <DetailItem
                        label="Dicipta"
                        value={formatDate(selectedCase.created_at)}
                        icon={<CalendarDays size={14} />}
                    />
                    <DetailItem
                        label="Dikemaskini"
                        value={formatDate(selectedCase.updated_at)}
                        icon={<CalendarDays size={14} />}
                    />
                    <DetailItem
                        label="Tarikh Akhir"
                        value={formatDate(selectedCase.due_at)}
                        icon={<Clock3 size={14} />}
                    />
                    <DetailItem
                        label="Lokasi"
                        value={selectedCase.location_name}
                        icon={<MapPin size={14} />}
                    />
                </div>

                <div
                    style={{
                        marginTop: 18,
                        display: "grid",
                        gridTemplateColumns: "1fr 360px",
                        gap: 16,
                        alignItems: "start",
                    }}
                >
                    <div
                        style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 22,
                            background: "#FFFFFF",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: 16,
                                borderBottom: `1px solid ${COLORS.border}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 17,
                                        fontWeight: 950,
                                        color: COLORS.text,
                                    }}
                                >
                                    Langkah & Bukti
                                </div>

                                <div
                                    style={{
                                        marginTop: 3,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: COLORS.muted,
                                    }}
                                >
                                    Semua submission, gambar, GPS, catatan dan lampiran.
                                </div>
                            </div>

                            <Badge
                                style={{
                                    background: COLORS.purpleBg,
                                    color: COLORS.purple,
                                    borderColor: "#DDD6FE",
                                }}
                            >
                                {steps.length} langkah
                            </Badge>
                        </div>

                        <div style={{ padding: 16, display: "grid", gap: 12 }}>
                            {steps.length === 0 ? (
                                <div
                                    style={{
                                        padding: 24,
                                        borderRadius: 16,
                                        background: "#F8FAFC",
                                        color: COLORS.muted,
                                        fontWeight: 800,
                                        textAlign: "center",
                                    }}
                                >
                                    Tiada langkah direkodkan.
                                </div>
                            ) : (
                                steps.map((step, index) => {
                                    const attachments = Array.isArray(step.attachments)
                                        ? step.attachments
                                        : [];

                                    return (
                                        <div
                                            key={step.uuid || `${step.step_key}-${index}`}
                                            style={{
                                                border: `1px solid ${COLORS.border}`,
                                                borderRadius: 18,
                                                padding: 14,
                                                background: "#F8FAFC",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: 12,
                                                    alignItems: "flex-start",
                                                    marginBottom: 12,
                                                }}
                                            >
                                                <div>
                                                    <div
                                                        style={{
                                                            fontSize: 15,
                                                            fontWeight: 950,
                                                            color: COLORS.text,
                                                        }}
                                                    >
                                                        {index + 1}. {step.step_label || step.label || step.step_key}
                                                    </div>

                                                    <div
                                                        style={{
                                                            marginTop: 4,
                                                            fontSize: 12,
                                                            fontWeight: 750,
                                                            color: COLORS.muted,
                                                        }}
                                                    >
                                                        {step.step_key || "-"} · {step.step_type || "-"}
                                                    </div>
                                                </div>

                                                <Badge style={statusStyle(step.status)}>
                                                    {formatStatus(step.status)}
                                                </Badge>
                                            </div>

                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                                    gap: 14,
                                                }}
                                            >
                                                <DetailItem
                                                    label="Diserah"
                                                    value={formatDate(step.submitted_at)}
                                                    icon={<CalendarDays size={14} />}
                                                />
                                                <DetailItem
                                                    label="Pegawai"
                                                    value={step.submitted_by || step.assigned_to}
                                                    icon={<User size={14} />}
                                                />

                                            </div>

                                            {step.remarks ? (
                                                <div
                                                    style={{
                                                        border: `1px solid ${COLORS.border}`,
                                                        borderRadius: 14,
                                                        padding: 12,
                                                        background: "#FFFFFF",
                                                        marginBottom: 12,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: COLORS.muted,
                                                            fontWeight: 950,
                                                            marginBottom: 5,
                                                        }}
                                                    >
                                                        Catatan
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: 13,
                                                            color: COLORS.text,
                                                            fontWeight: 800,
                                                            lineHeight: "20px",
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        {step.remarks}
                                                    </div>
                                                </div>
                                            ) : null}

                                            <div style={{ marginBottom: 12 }}>
                                                {renderPayload(step.submitted_payload)}
                                            </div>

                                            {attachments.length > 0 ? (
                                                <div
                                                    style={{
                                                        border: `1px solid ${COLORS.border}`,
                                                        borderRadius: 14,
                                                        padding: 12,
                                                        background: "#FFFFFF",
                                                    }}
                                                >
                                                    <StepLocationMap
                                                        latitude={step.latitude || step.submitted_payload?.latitude}
                                                        longitude={step.longitude || step.submitted_payload?.longitude}
                                                    />
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 7,
                                                            fontSize: 12,
                                                            color: COLORS.muted,
                                                            fontWeight: 950,
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        <ImageIcon size={14} />
                                                        Lampiran / Gambar Bukti
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                                            gap: 10,
                                                        }}
                                                    >
                                                        {attachments.map((attachment, attachmentIndex) => {
                                                            const unlockedUrl = attachmentPreviewMap[attachment.uuid];
                                                            const url = unlockedUrl || "";

                                                            const isUnlocking = attachmentLoadingMap[attachment.uuid];

                                                            const isImage =
                                                                Boolean(unlockedUrl) &&
                                                                (
                                                                    String(attachment?.mime_type || "").startsWith("image/") ||
                                                                    String(attachment?.file_type || "").startsWith("image/") ||
                                                                    String(attachment?.attachment_type || "").toLowerCase().includes("image") ||
                                                                    String(attachment?.type || "").toLowerCase().includes("photo") ||
                                                                    String(unlockedUrl).startsWith("data:image") ||
                                                                    /\.(jpg|jpeg|png|webp)(\?.*)?$/i.test(String(unlockedUrl || ""))
                                                                );
                                                            return (
                                                                <div
                                                                    key={attachment.uuid || attachmentIndex}
                                                                    style={{
                                                                        border: `1px solid ${COLORS.border}`,
                                                                        borderRadius: 13,
                                                                        overflow: "hidden",
                                                                        background: "#F8FAFC",
                                                                    }}
                                                                >
                                                                    {url && isImage ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                                                            style={{
                                                                                padding: 0,
                                                                                margin: 0,
                                                                                border: 0,
                                                                                width: "100%",
                                                                                background: "transparent",
                                                                                cursor: "pointer",
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={url}
                                                                                alt="Bukti"
                                                                                style={{
                                                                                    width: "100%",
                                                                                    height: 400,
                                                                                    objectFit: "cover",
                                                                                    display: "block",
                                                                                    background: "#F8FAFC",
                                                                                }}
                                                                            />
                                                                        </button>
                                                                    ) : (
                                                                        <div
                                                                            style={{
                                                                                height: 110,
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                color: COLORS.soft,
                                                                                background: "#FFFFFF",
                                                                            }}
                                                                        >
                                                                            {isUnlocking ? (
                                                                                <Loader2
                                                                                    size={28}
                                                                                    style={{
                                                                                        animation: "spin 1s linear infinite",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <ImageIcon size={30} />
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    <div
                                                                        style={{
                                                                            padding: 9,
                                                                            fontSize: 11,
                                                                            fontWeight: 850,
                                                                            color: COLORS.text,
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        {attachment.file_name ||
                                                                            attachment.filename ||
                                                                            attachment.attachment_type ||
                                                                            `Lampiran ${attachmentIndex + 1}`}
                                                                    </div>

                                                                    <div
                                                                        style={{
                                                                            padding: "0 9px 9px",
                                                                            display: "flex",
                                                                            gap: 7,
                                                                            flexWrap: "wrap",
                                                                        }}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            disabled={isUnlocking}
                                                                            onClick={() => unlockAttachment(attachment)}
                                                                            style={{
                                                                                border: `1px solid ${COLORS.border}`,
                                                                                background: "#FFFFFF",
                                                                                color: COLORS.blue,
                                                                                borderRadius: 10,
                                                                                padding: "7px 9px",
                                                                                fontSize: 11,
                                                                                fontWeight: 950,
                                                                                cursor: isUnlocking ? "not-allowed" : "pointer",
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: 6,
                                                                            }}
                                                                        >
                                                                            {isUnlocking ? (
                                                                                <Loader2
                                                                                    size={13}
                                                                                    style={{
                                                                                        animation: "spin 1s linear infinite",
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <Eye size={13} />
                                                                            )}
                                                                            Buka Gambar
                                                                        </button>

                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 22,
                            background: "#FFFFFF",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                padding: 16,
                                borderBottom: `1px solid ${COLORS.border}`,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 17,
                                    fontWeight: 950,
                                    color: COLORS.text,
                                }}
                            >
                                Aktiviti Kes
                            </div>

                            <div
                                style={{
                                    marginTop: 3,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: COLORS.muted,
                                }}
                            >
                                Audit trail tindakan kes.
                            </div>
                        </div>

                        <div style={{ padding: 14, display: "grid", gap: 10 }}>
                            {logs.length === 0 ? (
                                <div
                                    style={{
                                        padding: 18,
                                        borderRadius: 15,
                                        background: "#F8FAFC",
                                        color: COLORS.muted,
                                        fontWeight: 800,
                                        textAlign: "center",
                                    }}
                                >
                                    Tiada log.
                                </div>
                            ) : (
                                logs.map((log, index) => (
                                    <div
                                        key={log.uuid || index}
                                        style={{
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: 15,
                                            padding: 12,
                                            background: "#F8FAFC",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 950,
                                                color: COLORS.text,
                                            }}
                                        >
                                            {log.action || "Aktiviti"}
                                        </div>

                                        <div
                                            style={{
                                                marginTop: 4,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: COLORS.muted,
                                                lineHeight: "18px",
                                            }}
                                        >
                                            {log.message || "-"}
                                        </div>

                                        <div
                                            style={{
                                                marginTop: 7,
                                                fontSize: 11,
                                                fontWeight: 750,
                                                color: COLORS.soft,
                                            }}
                                        >
                                            {formatDate(log.created_at)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ background: COLORS.bg, minHeight: "100vh", padding: 22 }}>
            <style>
                {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @media (max-width: 900px) {
            .pbt-case-stats-grid {
              grid-template-columns: 1fr !important;
            }

            .pbt-case-filter-row {
              flex-direction: column !important;
              align-items: stretch !important;
            }
          }
            .glow-marker-wrap {
    position: relative;
    width: 34px;
    height: 34px;
}

.glow-marker-pulse {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: rgba(11, 77, 162, 0.22);
    animation: markerPulse 1.5s ease-out infinite;
}

.glow-marker-dot {
    position: absolute;
    top: 9px;
    left: 9px;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #0B4DA2;
    border: 3px solid #FFFFFF;
    box-shadow: 0 0 0 5px rgba(11, 77, 162, 0.16), 0 8px 18px rgba(15, 23, 42, 0.25);
}

@keyframes markerPulse {
    0% {
        transform: scale(0.55);
        opacity: 0.9;
    }
    100% {
        transform: scale(1.45);
        opacity: 0;
    }
}
        `}
            </style>

            <div style={{ maxWidth: 1480, margin: "0 auto" }}>
                <div
                    style={{
                        borderRadius: 26,
                        padding: 24,
                        color: "#FFFFFF",
                        background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.blue})`,
                        boxShadow: "0 18px 55px rgba(6,46,99,0.22)",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            right: -80,
                            top: -90,
                            width: 260,
                            height: 260,
                            borderRadius: 260,
                            background: "rgba(255,255,255,0.08)",
                        }}
                    />

                    <div
                        style={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 20,
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "7px 10px",
                                    borderRadius: 999,
                                    background: "rgba(255,255,255,0.12)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    fontSize: 12,
                                    fontWeight: 900,
                                    color: "#DBEAFE",
                                    marginBottom: 12,
                                }}
                            >
                                <ShieldCheck size={15} />
                                PBT OPERATIONS
                            </div>

                            <div
                                style={{
                                    fontSize: 30,
                                    fontWeight: 950,
                                    letterSpacing: -0.8,
                                }}
                            >
                                Senarai Kes & Rekod Operasi
                            </div>

                            <div
                                style={{
                                    marginTop: 8,
                                    color: "rgba(255,255,255,0.75)",
                                    fontSize: 14,
                                    fontWeight: 650,
                                    maxWidth: 780,
                                    lineHeight: "22px",
                                }}
                            >
                                Pantau semua kes mengikut PBT/sekolah, lihat status, bukti gambar,
                                lokasi GPS, submission langkah, lampiran dan audit trail.
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <SecondaryButton
                                onClick={() => loadCases(pagination.page || 1)}
                                icon={<RefreshCw size={16} />}
                            >
                                Muat Semula
                            </SecondaryButton>
                        </div>
                    </div>
                </div>

                <div
                    className="pbt-case-stats-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 14,
                        marginTop: 18,
                    }}
                >
                    <StatCard
                        title="Jumlah Kes"
                        value={metrics.total}
                        subtitle="Mengikut PBT / id_code"
                        color={COLORS.blue}
                        icon={<ClipboardList size={23} />}
                    />

                    <StatCard
                        title="Sedang Aktif"
                        value={metrics.open}
                        subtitle="Open / in progress"
                        color={COLORS.orange}
                        icon={<Clock3 size={23} />}
                    />

                    <StatCard
                        title="Selesai"
                        value={metrics.completed}
                        subtitle="Kes lengkap"
                        color={COLORS.green}
                        icon={<CheckCircle2 size={23} />}
                    />

                    <StatCard
                        title="Keutamaan Tinggi"
                        value={metrics.urgent}
                        subtitle="Urgent / high"
                        color={COLORS.red}
                        icon={<AlertTriangle size={23} />}
                    />
                </div>

                <div
                    style={{
                        marginTop: 18,
                        background: "#FFFFFF",
                        borderRadius: 24,
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
                        overflow: "hidden",
                    }}
                >
                    <div
                        className="pbt-case-filter-row"
                        style={{
                            padding: 16,
                            borderBottom: `1px solid ${COLORS.border}`,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 14,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 950, color: COLORS.text }}>
                                Senarai Kes
                            </div>

                            <div
                                style={{
                                    marginTop: 3,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: COLORS.muted,
                                }}
                            >
                                {schoolId
                                    ? `Data dimuatkan untuk: ${schoolId}`
                                    : "Tiada id_code."}
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <div
                                style={{
                                    height: 40,
                                    borderRadius: 13,
                                    border: `1px solid ${COLORS.border}`,
                                    padding: "0 11px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    minWidth: 280,
                                    background: "#FFFFFF",
                                }}
                            >
                                <Search size={16} color={COLORS.soft} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Cari kes, rujukan, lokasi..."
                                    style={{
                                        border: 0,
                                        outline: 0,
                                        flex: 1,
                                        fontWeight: 700,
                                        color: COLORS.text,
                                    }}
                                />
                            </div>

                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                style={{
                                    height: 40,
                                    borderRadius: 13,
                                    border: `1px solid ${COLORS.border}`,
                                    padding: "0 11px",
                                    background: "#FFFFFF",
                                    color: COLORS.text,
                                    fontWeight: 800,
                                    outline: 0,
                                }}
                            >
                                <option value="">Semua Status</option>
                                <option value="OPEN">Dibuka</option>
                                <option value="IN_PROGRESS">Sedang Diproses</option>
                                <option value="COMPLETED">Selesai</option>
                                <option value="REJECTED">Ditolak</option>
                                <option value="CANCELLED">Dibatalkan</option>
                            </select>

                            <select
                                value={priorityFilter}
                                onChange={(e) => setPriorityFilter(e.target.value)}
                                style={{
                                    height: 40,
                                    borderRadius: 13,
                                    border: `1px solid ${COLORS.border}`,
                                    padding: "0 11px",
                                    background: "#FFFFFF",
                                    color: COLORS.text,
                                    fontWeight: 800,
                                    outline: 0,
                                }}
                            >
                                <option value="">Semua Prioriti</option>
                                <option value="URGENT">Segera</option>
                                <option value="HIGH">Tinggi</option>
                                <option value="MEDIUM">Sederhana</option>
                                <option value="LOW">Rendah</option>
                            </select>

                            <button
                                type="button"
                                onClick={() => setOnlyMine((prev) => !prev)}
                                style={{
                                    height: 40,
                                    borderRadius: 13,
                                    border: `1px solid ${onlyMine ? "#BFDBFE" : COLORS.border}`,
                                    padding: "0 12px",
                                    background: onlyMine ? "#EFF6FF" : "#FFFFFF",
                                    color: onlyMine ? COLORS.blue : COLORS.text,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 7,
                                }}
                            >
                                <Filter size={15} />
                                Rekod Saya
                            </button>
                        </div>
                    </div>

                    <div style={{ padding: 16 }}>{renderCases()}</div>

                    <div
                        style={{
                            padding: 16,
                            borderTop: `1px solid ${COLORS.border}`,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: COLORS.muted,
                            }}
                        >
                            Page {pagination.page || 1} · Total {pagination.total || 0}
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                            <SecondaryButton
                                disabled={loading || Number(pagination.page || 1) <= 1}
                                onClick={() => loadCases(Number(pagination.page || 1) - 1)}
                            >
                                Sebelum
                            </SecondaryButton>

                            <SecondaryButton
                                disabled={loading || !pagination.has_more}
                                onClick={() => loadCases(Number(pagination.page || 1) + 1)}
                            >
                                Seterusnya
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            </div>

            <ModalShell
                open={showDetailModal}
                onClose={closeCaseDetail}
                title={
                    selectedCase
                        ? selectedCase.reference_no || selectedCase.title || "Butiran Kes"
                        : "Butiran Kes"
                }
                subtitle="Maklumat penuh kes, langkah workflow, bukti, lokasi dan audit trail."
            >
                {renderCaseDetail()}
            </ModalShell>
        </div>
    );
}